import { Box, Typography } from '@mui/material';
import { useGetIdentity } from 'react-admin';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader } from './Loader';

/**
 * Keeps a user inside the modules they are allowed to open.
 *
 * A module the user has no permission for is not an error page — it is simply
 * not part of their portal, so they are redirected to the first module they do
 * have (the same one the sidebar lists first). Nothing announces that another
 * module exists.
 *
 * The check is by route path against the sections the server returned, so a
 * module added on the backend is guarded automatically — there is no
 * route-to-permission table to keep in sync here.
 *
 * This is navigation, not the security boundary: every cloud function behind
 * these pages re-checks the same permission server-side.
 */

// Roles that hold every section by definition. They are never section-checked
// here, so a slow or failed permission load can never lock them out of their
// own panel.
const UNRESTRICTED_ROLES = ['Super-User', 'Manager'];

export const SectionGuard = ({ children }) => {
  const { data: identity, isLoading } = useGetIdentity();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <Loader />
      </Box>
    );
  }

  if (UNRESTRICTED_ROLES.includes(identity?.role)) {
    return children;
  }

  const sections = identity?.sections || [];

  // An empty list means the permissions never loaded, not that everything is
  // forbidden. Wait rather than redirecting somewhere arbitrary.
  if (sections.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <Loader />
      </Box>
    );
  }

  // Match the longest declared path that prefixes the current route, so nested
  // routes under a section (e.g. /operators/123) stay inside that section.
  const matched = sections
    .filter((section) => section.path && location.pathname.startsWith(section.path))
    .sort((a, b) => b.path.length - a.path.length)[0];

  if (matched) {
    return children;
  }

  // Not their module — send them to their first one. Profile is granted to
  // everyone, so there is always a destination.
  const fallback = sections.find((section) => section.path);

  if (fallback && fallback.path !== location.pathname) {
    return <Navigate to={fallback.path} replace />;
  }

  // Only reachable if the user somehow has sections but none with a route.
  return (
    <Box sx={{ p: 4 }}>
      <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
        No modules are available for your account. Contact a Super-User.
      </Typography>
    </Box>
  );
};

export default SectionGuard;
